/**
 * Schema migration runner. "The save IS the world" — never lose data.
 *
 * A migration upgrades a save exactly one version forward. The runner chains them
 * from a save's `schemaVersion` up to `SCHEMA_VERSION`, applying each registered
 * step in order. Migrations are added to MIGRATIONS (keyed by the version they
 * upgrade FROM) whenever the Save shape changes.
 */
import type { Save, SaveMigration, MoveSlot, SpeciesData, MoveData } from '@/shared';
import { SCHEMA_VERSION, emptyQuestState, emptyCareState, emptyLeagueState } from '@/shared';
import { naturalLearnsetUpToLevel } from '@/core';

/**
 * Species/move lookups the moveset-fill migration (v4 → v5) needs. The migration runs
 * inside the (synchronous) save load path, BEFORE a GameSession exists, so the dataset
 * is injected via {@link setMigrationDataLookup} at app boot (right after the dataset is
 * loaded). When unset — a fixtures-only test, or the dataset isn't ready — the migration
 * still runs but only normalizes `uses` (it KEEPS existing moves, never drops data); the
 * GameSession then completes the natural-moveset fill idempotently on load via the same
 * pure {@link fillNaturalMoveset} helper. Either path yields the identical result.
 */
export interface MigrationDataLookup {
  speciesById(id: string): SpeciesData | undefined;
  moveById(id: string): MoveData | undefined;
}

let migrationData: MigrationDataLookup | undefined;

/** Wire the dataset into the moveset-fill migration. Call once at app boot. */
export function setMigrationDataLookup(lookup: MigrationDataLookup | undefined): void {
  migrationData = lookup;
}

/**
 * v1 → v2: the PLAYER became a (then-human) trainer with a chosen follower.
 *
 * Back-fills `profile.avatar` (the v2 human-chibi `{ gender, variant }` shape) and
 * `profile.followerSpeciesId` (the first team member — the starter — walks behind
 * the player). v1 never stored a Pokemon player-avatar field on the Save (the old
 * `avatarSpeciesId` only seeded the dex), so there is nothing to drop — we only add
 * the two new profile fields. All other data is preserved verbatim. The avatar shape
 * here is the v2 contract; the v2 → v3 step then maps it to a trainerKey.
 */
const migrateV1toV2: SaveMigration = (old) => {
  const o = (old ?? {}) as Record<string, unknown>;
  const profile = (o.profile ?? {}) as Record<string, unknown>;
  const team = Array.isArray(o.team) ? (o.team as Array<{ speciesId?: string }>) : [];
  const follower =
    typeof profile.followerSpeciesId === 'string' || profile.followerSpeciesId === null
      ? (profile.followerSpeciesId as string | null)
      : (team[0]?.speciesId ?? null);

  return {
    ...(o as object),
    schemaVersion: 2,
    profile: {
      ...profile,
      avatar:
        profile.avatar && typeof profile.avatar === 'object'
          ? profile.avatar
          : { gender: 'male', variant: 0 },
      followerSpeciesId: follower,
    },
  } as Save;
};

/**
 * v2 → v3: the PLAYER avatar became a REAL Pokemon-game TRAINER (a Showdown
 * `trainerKey`), replacing the old human-chibi `{ gender, variant }` shape.
 *
 * Maps the old gender to a sensible default trainer sprite (male → "red", female →
 * "lyra"); any other / missing shape falls back to the default. `followerSpeciesId`
 * and all other data are preserved verbatim. (Both keys are vendored by the ingest;
 * a missing sprite still resolves to the renderer's neutral placeholder.)
 */
const migrateV2toV3: SaveMigration = (old) => {
  const o = (old ?? {}) as Record<string, unknown>;
  const profile = (o.profile ?? {}) as Record<string, unknown>;
  const oldAvatar = (profile.avatar ?? {}) as Record<string, unknown>;
  const gender = oldAvatar.gender;
  const trainerKey =
    typeof oldAvatar.trainerKey === 'string'
      ? oldAvatar.trainerKey
      : gender === 'female'
        ? 'lyra'
        : 'red';

  return {
    ...(o as object),
    schemaVersion: 3,
    profile: {
      ...profile,
      avatar: { trainerKey },
    },
  } as Save;
};

/**
 * v3 → v4: SAVES BECAME MULTI-SLOT (D16). New Game now always creates a NEW save
 * rather than overwriting the single live slot, so every save needs a stable,
 * unique identity.
 *
 * Back-fills `profile.slotId` (a generated id; a legacy single save migrates into
 * its own slot and is never lost) and `profile.createdAt` (defaults to
 * `lastPlayedAt` when a pre-`createdAt` save somehow lacks it). All other data is
 * preserved verbatim.
 */
const migrateV3toV4: SaveMigration = (old) => {
  const o = (old ?? {}) as Record<string, unknown>;
  const profile = (o.profile ?? {}) as Record<string, unknown>;

  const createdAt =
    typeof profile.createdAt === 'number'
      ? profile.createdAt
      : typeof o.lastPlayedAt === 'number'
        ? (o.lastPlayedAt as number)
        : Date.now();

  const slotId =
    typeof profile.slotId === 'string' && profile.slotId.length > 0
      ? profile.slotId
      : `slot-${createdAt}`;

  return {
    ...(o as object),
    schemaVersion: 4,
    profile: {
      ...profile,
      slotId,
      createdAt,
    },
  } as Save;
};

/**
 * Bring one owned mon's moveset up to its FULL natural level-up learnset (UNLIMITED
 * MOVES). Idempotent + non-destructive — the load-time guarantee behind both the v4 → v5
 * migration and the GameSession's on-load backfill:
 *
 *  - KEEPS every existing slot verbatim (its `pp`, `maxPp`, and `uses` are preserved);
 *  - ADDS any natural level-up move (up to the mon's current level) it is missing, with
 *    full PP from the move data (a fallback of 5 if the move is unknown) and `uses: 0`;
 *  - back-fills `uses: 0` on any existing slot that lacks it.
 *
 * Re-running yields the same array (added moves are already present → no duplicates; the
 * uses normalize is a no-op the second time). Never removes or reorders existing moves —
 * new natural moves are appended after what the mon already had.
 *
 * Mutates `mon.moves` in place and returns it. With no `data` lookup (or an unknown
 * species) it only normalizes `uses`, so the worst case still never loses data.
 */
export function fillNaturalMoveset(
  mon: { speciesId: string; level: number; moves: MoveSlot[] },
  data?: MigrationDataLookup,
): MoveSlot[] {
  const moves = Array.isArray(mon.moves) ? mon.moves : [];

  // Normalize: every existing slot gets a `uses` (absent === 0); other fields untouched.
  const normalized: MoveSlot[] = moves.map((slot) => ({
    ...slot,
    uses: typeof slot.uses === 'number' ? slot.uses : 0,
  }));

  const species = data?.speciesById(mon.speciesId);
  if (!species) {
    mon.moves = normalized;
    return normalized;
  }

  const known = new Set(normalized.map((s) => s.moveId));
  for (const moveId of naturalLearnsetUpToLevel(species, mon.level)) {
    if (known.has(moveId)) continue;
    known.add(moveId);
    const maxPp = data?.moveById(moveId)?.pp ?? 5;
    normalized.push({ moveId, pp: maxPp, maxPp, uses: 0 });
  }

  mon.moves = normalized;
  return normalized;
}

/**
 * v4 → v5: UNLIMITED MOVESETS + the per-slot `uses` counter.
 *
 * Earlier saves capped a mon at four moves (extra level-up moves were silently lost) and
 * had no `uses` field. This fixup, for EVERY owned mon (the team AND all boxes), fills
 * each moveset with its FULL natural level-up learnset up to its CURRENT level — so every
 * existing mon immediately gains the moves it should have known all along — and adds
 * `uses: 0` to every slot. See {@link fillNaturalMoveset}: it KEEPS existing moves + their
 * PP + uses (correct PP for added moves) and is safe + idempotent (re-running never drops
 * data nor duplicates a move). When the dataset isn't injected yet the slots are still
 * `uses`-normalized and the GameSession completes the learnset fill on load.
 */
const migrateV4toV5: SaveMigration = (old) => {
  const o = (old ?? {}) as Record<string, unknown>;

  const fixMon = (m: unknown): unknown => {
    if (!m || typeof m !== 'object') return m;
    const mon = m as { speciesId?: unknown; level?: unknown; moves?: unknown };
    if (typeof mon.speciesId !== 'string') return m;
    const moves = Array.isArray(mon.moves) ? (mon.moves as MoveSlot[]) : [];
    const level = typeof mon.level === 'number' ? mon.level : 1;
    const filled = fillNaturalMoveset(
      { speciesId: mon.speciesId, level, moves: moves.map((s) => ({ ...s })) },
      migrationData,
    );
    return { ...(m as object), moves: filled };
  };

  const team = Array.isArray(o.team) ? o.team.map(fixMon) : o.team;
  const boxes = Array.isArray(o.boxes)
    ? o.boxes.map((b) => {
        if (!b || typeof b !== 'object') return b;
        const box = b as Record<string, unknown>;
        const contents = Array.isArray(box.contents) ? box.contents.map(fixMon) : box.contents;
        return { ...box, contents };
      })
    : o.boxes;

  return {
    ...(o as object),
    schemaVersion: 5,
    team,
    boxes,
  } as Save;
};

/**
 * v5 → v6: the QUEST/STORY layer (D17). Adds `quests: QuestState` to the Save.
 *
 * Pure + non-destructive (mirrors {@link migrateV3toV4}): back-fills an empty quest
 * block — `{ active: [], completed: [], badges: [] }` — onto any pre-quest save and
 * preserves every other field verbatim. Quests store PROGRESS only (authored defs
 * live in data/quests/), so a save that predates a newly-authored quest picks it up
 * automatically on load — no migration per content drop. A save that somehow already
 * carries a `quests` block keeps it (idempotent).
 */
const migrateV5toV6: SaveMigration = (old) => {
  const o = (old ?? {}) as Record<string, unknown>;
  const existing = o.quests;
  const quests =
    existing && typeof existing === 'object' && !Array.isArray(existing)
      ? existing
      : emptyQuestState();

  return {
    ...(o as object),
    schemaVersion: 6,
    quests,
  } as Save;
};

/**
 * v6 → v7: the FOUR Round-3 arc blocks (notes/r3-design/00-BUILD-ORDER-AND-MIGRATION.md),
 * combined into ONE bump so the arcs never race each other on the schema. All optional
 * on Save; back-filled to their empty values here so a v6 save upgrades losslessly:
 *   care      → emptyCareState()   (follower-care side-table)
 *   league    → emptyLeagueState() (Elite Four / Hall of Fame)
 *   rival     → null               (resolved lazily on the first meeting)
 *   overworld → null               (null ⇒ spawn at the area default)
 * Idempotent: a save that already carries a block keeps it verbatim.
 */
const migrateV6toV7: SaveMigration = (old) => {
  const o = (old ?? {}) as Record<string, unknown>;
  const keep = (v: unknown): boolean => v != null && typeof v === 'object' && !Array.isArray(v);
  return {
    ...(o as object),
    schemaVersion: 7,
    care: keep(o.care) ? o.care : emptyCareState(),
    league: keep(o.league) ? o.league : emptyLeagueState(),
    rival: keep(o.rival) ? o.rival : null,
    overworld: keep(o.overworld) ? o.overworld : null,
  } as Save;
};

/**
 * Registry of migrations keyed by the version they upgrade FROM. Add `0 -> 1`,
 * `1 -> 2`, … here as the shape evolves.
 */
export const MIGRATIONS: Readonly<Record<number, SaveMigration>> = {
  1: migrateV1toV2,
  2: migrateV2toV3,
  3: migrateV3toV4,
  4: migrateV4toV5,
  5: migrateV5toV6,
  6: migrateV6toV7,
};

/**
 * The active (runtime) migration table the load/import paths consult. Seeded from
 * MIGRATIONS; tests may register extra steps onto it (and reset it) via the store's
 * `__testing` hooks without mutating the frozen production map.
 */
let activeMigrations: Record<number, SaveMigration> = { ...MIGRATIONS };

/** Pull the on-disk schema version off a raw stored value (defaults to 0). */
export function readVersion(raw: unknown): number {
  if (raw && typeof raw === 'object' && 'schemaVersion' in raw) {
    const v = (raw as { schemaVersion: unknown }).schemaVersion;
    if (typeof v === 'number' && Number.isInteger(v)) return v;
  }
  return 0;
}

/**
 * Chain migrations from `fromVersion` up to `SCHEMA_VERSION`. Throws if a step is
 * missing (a save we can't safely upgrade), the save is from the future, or a step
 * fails to advance the version. `migrations` is injectable for testing; it defaults
 * to the active runtime table.
 */
export function migrate(
  raw: unknown,
  fromVersion: number = readVersion(raw),
  migrations: Record<number, SaveMigration> = activeMigrations,
): Save {
  if (!Number.isInteger(fromVersion) || fromVersion < 0) {
    throw new Error(`migrate: invalid fromVersion ${String(fromVersion)}`);
  }
  if (fromVersion > SCHEMA_VERSION) {
    throw new Error(
      `migrate: save is from a newer version (${fromVersion}) than supported (${SCHEMA_VERSION})`,
    );
  }

  let current: unknown = raw;
  let version = fromVersion;
  while (version < SCHEMA_VERSION) {
    const step = migrations[version];
    if (!step) {
      throw new Error(`migrate: no migration registered for version ${version}`);
    }
    const next = step(current);
    const nextVersion = readVersion(next);
    if (nextVersion <= version) {
      throw new Error(
        `migrate: migration from ${version} did not advance the version (got ${nextVersion})`,
      );
    }
    current = next;
    version = nextVersion;
  }

  return current as Save;
}

/** TEST ONLY: register an extra migration step onto the active table. */
export function _registerMigration(fromVersion: number, fn: SaveMigration): void {
  activeMigrations[fromVersion] = fn;
}

/** TEST ONLY: reset the active table back to the production MIGRATIONS. */
export function _resetMigrations(): void {
  activeMigrations = { ...MIGRATIONS };
}
